import { ChartFigure } from '../ChartFigure/ChartFigure'
import { areaPath, linePath } from '../geometry'
import type { Point } from '../geometry'
import { formatCount } from '../../format'
import palette from '../series.module.css'
import styles from './Sparkline.module.css'

/**
 * A line with no axes, sized to sit beside a figure rather than to be read on
 * its own.
 *
 * The scale runs from the smallest value to the largest, not from zero. That is
 * the one real decision in this file and it is a trade with a known cost: it
 * shows the shape of a trend at the expense of its magnitude, so a series
 * wobbling between 1,000 and 1,004 draws the same dramatic peak as one that
 * doubled. A sparkline is an adjective, not a measurement, and the measurement
 * is the number printed next to it, which is why this component is almost
 * always inside a `Metric`. Where the magnitude is the point, use a LineChart
 * with a zero baseline.
 *
 * The hidden table is what stops the exaggeration from being a lie: whatever
 * the line implies, the numbers are one tab away and are exact.
 */

export interface SparklineProps {
  values: readonly number[]
  /** One per value: a week, a date, a period. Used by the hidden table. */
  labels?: readonly string[]
  /** Names the series for a screen reader. Not drawn unless `titleVisible`. */
  title: string
  titleVisible?: boolean
  /** Column heading for the figures in the hidden table. */
  valueHeading?: string
  periodHeading?: string
  seriesIndex?: number
  /** A tinted area under the line. Off inside a dense metric row. */
  area?: boolean
  locale?: string
  className?: string
}

/*
 * The coordinate space. Not pixels: the SVG scales to whatever box it lands in,
 * and this is only the ratio between the line's length and its height. Twelve
 * weeks of data in a strip four times as wide as it is tall.
 */
const WIDTH = 120
const HEIGHT = 32
const INSET = 2

export function Sparkline({
  values,
  labels,
  title,
  titleVisible = false,
  valueHeading = title,
  periodHeading = '',
  seriesIndex = 0,
  area = false,
  locale,
  className,
}: SparklineProps) {
  const lowest = values.length > 0 ? Math.min(...values) : 0
  const highest = values.length > 0 ? Math.max(...values) : 0
  // A flat series has no range to scale against, and dividing by it would put
  // the whole line at NaN. It draws down the middle instead, which is honest.
  const span = highest - lowest || 1

  const points: Point[] = values.map((value, index) => ({
    x: INSET + (index / Math.max(values.length - 1, 1)) * (WIDTH - INSET * 2),
    y:
      values.length > 0 && highest === lowest
        ? HEIGHT / 2
        : HEIGHT - INSET - ((value - lowest) / span) * (HEIGHT - INSET * 2),
  }))

  const last = points[points.length - 1]

  return (
    <ChartFigure
      title={title}
      titleVisible={titleVisible}
      className={[styles.sparkline, className].filter(Boolean).join(' ')}
      table={{
        columns: [periodHeading, valueHeading],
        rows: values.map((value, index) => ({
          header: labels?.[index] ?? String(index + 1),
          cells: [formatCount(value, locale)],
        })),
      }}
    >
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
        <g className={palette.series} data-series={seriesIndex}>
          {area && points.length > 1 ? (
            <path d={areaPath(points, HEIGHT)} className={styles.area} />
          ) : null}
          <path d={linePath(points)} className={[palette.stroke, styles.line].join(' ')} />
          {/* The end of the line is where the eye is trying to land, so it gets
              a mark. Without it a rising series and a falling one are told
              apart only by tracing the whole line. */}
          {last ? <circle cx={last.x} cy={last.y} r={2} className={palette.fill} /> : null}
        </g>
      </svg>
    </ChartFigure>
  )
}
